import type { CachedCredentialDto } from '@terra/shared-contracts-authenticator';
import { isJwt, JWT_SYNC_HEADERS } from '@terra/shared-utils';
import type { ApplicationCredential } from '../../hooks/useApplicationCredentials';
import { isUnexpectedLoginLanding } from '../../electron-shared/utils/isUnexpectedLoginLanding';

export { isUnexpectedLoginLanding };

export type BrowserLaunchReadiness = 'ready' | 'reverify';

type StorageItems = Array<{ key: string; value: string }>;

export type BrowserInjectionCredentialData = {
  headers: CachedCredentialDto['headers'];
  cookies: CachedCredentialDto['cookies'];
  secrets: CachedCredentialDto['secrets'];
  localStorage: StorageItems;
  sessionStorage: StorageItems;
};

export type BrowserInjectionPlan = {
  credentials: BrowserInjectionCredentialData;
  targetUrl: string;
  readiness: BrowserLaunchReadiness | null;
  successToastDescription: string;
};

function hasFullBrowserStorage(
  cached: CachedCredentialDto | undefined,
): boolean {
  if (!cached?.fullBrowserStorage) return false;

  return (
    cached.fullBrowserStorage.localStorage.length > 0 ||
    cached.fullBrowserStorage.sessionStorage.length > 0
  );
}

export function getBrowserLaunchReadiness(
  cached: CachedCredentialDto | undefined,
): BrowserLaunchReadiness | null {
  if (hasFullBrowserStorage(cached)) {
    return 'ready';
  }

  if (hasCachedAuthArtifacts(cached)) {
    return 'reverify';
  }

  return null;
}

export function hasCachedAuthArtifacts(
  cached: CachedCredentialDto | undefined,
): boolean {
  if (!cached) {
    return false;
  }

  return (
    cached.headers.length > 0 ||
    cached.cookies.length > 0 ||
    cached.secrets.length > 0 ||
    hasFullBrowserStorage(cached)
  );
}

export function isCredentialBrowserLaunchable({
  credential,
  cached,
}: {
  credential: ApplicationCredential;
  cached: CachedCredentialDto | undefined;
}): boolean {
  if (credential.isDisabled) {
    return false;
  }

  if (credential.verification?.status !== 'VERIFIED') {
    return false;
  }

  const hasCredentialMtls =
    Boolean(credential.mtlsPfx) ||
    Boolean(credential.mtlsCert && credential.mtlsKey);

  return hasCachedAuthArtifacts(cached) || hasCredentialMtls;
}

const STORAGE_AUTH_TOKEN_KEYS: readonly string[] = [
  'access_token',
  'accesstoken',
  'access-token',
  'token',
  'id_token',
  'idtoken',
];

const SESSION_AUTH_COOKIE_NAMES: readonly string[] = [
  'session',
  'sessionid',
  'jsessionid',
  'connect.sid',
  'auth',
  'authtoken',
  'access_token',
  'id_token',
  'sid',
  'token',
];

function storageHasAuthMaterial(items: StorageItems): boolean {
  return items.some((item) => {
    const lowerKey = item.key.toLowerCase();

    return (
      isJwt(item.value) ||
      STORAGE_AUTH_TOKEN_KEYS.includes(lowerKey) ||
      SESSION_AUTH_COOKIE_NAMES.includes(lowerKey)
    );
  });
}

function cookiesIncludeAuthMaterial(
  cookies: CachedCredentialDto['cookies'],
): boolean {
  return cookies.some((cookie) => {
    const lowerName = cookie.name.toLowerCase();

    return isJwt(cookie.value) || SESSION_AUTH_COOKIE_NAMES.includes(lowerName);
  });
}

function shouldPreferSessionMaterialOverTokenHeaders({
  readiness,
  localStorage,
  sessionStorage,
  cookies,
}: {
  readiness: BrowserLaunchReadiness | null;
  localStorage: StorageItems;
  sessionStorage: StorageItems;
  cookies: CachedCredentialDto['cookies'];
}): boolean {
  if (readiness !== 'ready') {
    return false;
  }

  return (
    storageHasAuthMaterial(localStorage) ||
    storageHasAuthMaterial(sessionStorage) ||
    cookiesIncludeAuthMaterial(cookies)
  );
}

export function filterHeadersForBrowserStorageInjection({
  headers,
  readiness,
  localStorage,
  sessionStorage,
  cookies,
}: {
  headers: CachedCredentialDto['headers'];
  readiness: BrowserLaunchReadiness | null;
  localStorage: StorageItems;
  sessionStorage: StorageItems;
  cookies: CachedCredentialDto['cookies'];
}): CachedCredentialDto['headers'] {
  if (
    !shouldPreferSessionMaterialOverTokenHeaders({
      readiness,
      localStorage,
      sessionStorage,
      cookies,
    })
  ) {
    return headers;
  }

  return headers.filter(
    (header) => !JWT_SYNC_HEADERS.includes(header.key.toLowerCase()),
  );
}

export function buildBrowserInjectionPlan({
  credential,
  cached,
  applicationUrl,
  credentialTargetUrl,
}: {
  credential: ApplicationCredential;
  cached: CachedCredentialDto | undefined;
  applicationUrl: string | undefined;
  credentialTargetUrl: string | undefined;
}): BrowserInjectionPlan {
  const readiness = getBrowserLaunchReadiness(cached);
  const authenticatedAs = credential.role || credential.username;

  const localStorage = cached?.fullBrowserStorage?.localStorage ?? [];
  const sessionStorage = cached?.fullBrowserStorage?.sessionStorage ?? [];
  const rawHeaders = cached?.headers ?? credential.headers ?? [];
  const cookies = cached?.cookies ?? credential.cookies ?? [];

  const credentials: BrowserInjectionCredentialData = {
    headers: filterHeadersForBrowserStorageInjection({
      headers: rawHeaders,
      readiness,
      localStorage,
      sessionStorage,
      cookies,
    }),
    cookies,
    secrets:
      readiness === 'ready'
        ? []
        : (cached?.secrets ?? credential.secrets ?? []),
    localStorage,
    sessionStorage,
  };

  const fallbackUrl = applicationUrl ?? credentialTargetUrl ?? 'about:blank';
  const targetUrl =
    readiness === 'ready'
      ? fallbackUrl
      : (credentialTargetUrl ?? applicationUrl ?? 'about:blank');

  const successToastDescription =
    readiness === 'reverify'
      ? `Authenticated as ${authenticatedAs}. Using auth tokens only. Re-verify if you land on login.`
      : `Authenticated as ${authenticatedAs}`;

  return {
    credentials,
    targetUrl,
    readiness,
    successToastDescription,
  };
}
